import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toNumber } from '@/lib/decimal'
import { formatHmm } from '@/lib/hours'
import type { WeekTotal } from '@/lib/types'
import { cn } from '@/lib/utils'
import { isoWeekId, shiftWeekId, weekMetaFromId } from '@/lib/week'
import { PeriodPicker } from './PeriodPicker'

interface WeekNavProps {
  weekId: string
  onChange: (weekId: string) => void
}

export function WeekNav({ weekId, onChange }: WeekNavProps) {
  const meta = weekMetaFromId(weekId)
  const currentWeekId = isoWeekId(new Date())

  return (
    <>
      <div className="inline-flex items-center gap-1 rounded-lg border border-hairline bg-surface p-0.5">
        <button
          type="button"
          onClick={() => onChange(shiftWeekId(weekId, -1))}
          className="btn-ghost h-8 w-8 p-0"
          aria-label="Vorige week"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <PeriodPicker
          mode="week"
          label={meta.label}
          selected={meta.start}
          onPick={(date) => onChange(isoWeekId(date))}
        />
        <button
          type="button"
          onClick={() => onChange(shiftWeekId(weekId, 1))}
          className="btn-ghost h-8 w-8 p-0"
          aria-label="Volgende week"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={() => onChange(currentWeekId)}
        disabled={weekId === currentWeekId}
        title={weekId === currentWeekId ? 'Je zit al in de huidige week' : undefined}
        className="btn-ghost h-8 px-2.5 text-sm"
      >
        Deze week
      </button>
    </>
  )
}

interface WeekTotalsStripProps {
  totals: WeekTotal[]
  weekId: string
  onChange: (weekId: string) => void
}

export function WeekTotalsStrip({ totals, weekId, onChange }: WeekTotalsStripProps) {
  const max = Math.max(40, ...totals.map((week) => toNumber(week.total)))
  const currentWeekId = isoWeekId(new Date())

  return (
    <div className="flex h-8 items-end gap-1 pb-1.5 pt-1" role="group" aria-label="Uren per week">
      {totals.map((week) => {
        const hours = toNumber(week.total)
        const selected = week.weekId === weekId
        const isCurrent = week.weekId === currentWeekId
        const height = hours > 0 ? Math.max(5, Math.round((hours / max) * 18)) : 3
        const label = weekMetaFromId(week.weekId).label
        return (
          <button
            key={week.weekId}
            type="button"
            onClick={() => onChange(week.weekId)}
            title={`${label} · ${hours > 0 ? `${formatHmm(hours)} uur` : 'leeg'}`}
            aria-label={`${label}, ${hours > 0 ? `${formatHmm(hours)} uur` : 'leeg'}`}
            aria-pressed={selected}
            className="group flex h-full w-3 flex-col items-center justify-end rounded-xs hover:bg-fill-2"
          >
            <span
              style={{ height }}
              className={cn(
                'w-1.5 rounded-full transition-colors',
                selected ? 'bg-accent-600' : hours > 0 ? 'bg-ink-faint/50 group-hover:bg-ink-faint' : 'bg-fill-3',
                isCurrent && !selected && 'ring-1 ring-accent-600/40',
              )}
            />
          </button>
        )
      })}
    </div>
  )
}
